import React, { useState, useEffect } from 'react';
import QRCode from "react-qr-code";
import { useSearchParams, useNavigate } from 'react-router-dom';
import PurchaseCardModal from '../components/PurchaseCardModal';
import axios from 'axios'; // Imported for reliable API calls
import '../styles/DiscountCard.css';

const DiscountCard = () => {
  // --- STATE ---
  const [isGoldMember, setIsGoldMember] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [cardData, setCardData] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // --- HELPER: GET USER ID ---
  const getUserId = () => {
    const uString = localStorage.getItem('userId') ||
      localStorage.getItem('user') ||
      localStorage.getItem('DDoctorUser');
    if (!uString) return null;
    try {
      if (uString.includes("{")) {
        const u = JSON.parse(uString);
        return u._id || u.id;
      }
    } catch (e) { console.error("Parsing User ID error", e); }
    return uString;
  };

  // --- EFFECT 1: CHECK DB ON LOAD ---
  useEffect(() => {
    const userId = getUserId();
    if (userId) {
      // FIX: Used axios and correct string interpolation
      axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/cards/user/${userId}`)
        .then(response => {
          if (response.data.success && response.data.data) {
            setCardData(response.data.data);
            if (response.data.data.status && response.data.data.status.toLowerCase() === 'active') {
              setIsGoldMember(true);
            } else {
              setIsGoldMember(false);
            }
          }
        })
        .catch(err => {
          // It's normal to have no card if not bought yet
          console.log("Status: Not a Gold Member yet.");
        });
    }
  }, []);

  // --- EFFECT 2: HANDLE STRIPE RETURN ---
  useEffect(() => {
    const isSuccess = searchParams.get('payment_success');
    const userId = getUserId();

    // Only run this if we have success flag AND user is not yet marked as member locally
    if (isSuccess && userId) {

      axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/cards/confirm-payment`, {
        userId: userId
      })
        .then(res => {
          if (res.data.success) {
            // Update State Immediately
            setIsGoldMember(true);

            // Fetch the new card data to display it
            return axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/cards/user/${userId}`);
          }
        })
        .then(response => {
          if (response && response.data.success) {
            setCardData(response.data.data);
            // Remove the ?payment_success=true from URL so refreshing doesn't re-trigger
            navigate('/discount-card', { replace: true });
            alert("Payment Verified! Your Gold Card is active.");
          }
        })
        .catch(err => console.error("Payment Confirmation Error:", err));
    }
  }, [searchParams, navigate]);

  return (
    <div className="container mt-5">

      {/* =========================================================
          SECTION 1: NOT A GOLD MEMBER YET
          (Check if they are "Pending" or "New")
         ========================================================= */}
      {!isGoldMember && (
        <>
          {/* STATE A: PENDING PAYMENT (Card exists but not paid) */}
          {cardData && cardData.status === 'Pending Payment' ? (
            <div className="container text-center mt-5 mb-5">
              <div className="alert alert-warning shadow-sm p-4 mx-auto" style={{ maxWidth: '600px' }}>
                <h2 className="fw-bold text-dark mb-3">⚠️ Payment Pending</h2>
                <p className="lead">
                  You have generated card <strong>{cardData.cardNumber}</strong>, but the payment was not completed.
                </p>
                <hr />
                <p className="mb-4">Please complete your payment to activate your Gold Membership.</p>
                
                <button 
                  className="btn btn-primary btn-lg w-100"
                  onClick={() => setShowModal(true)}
                  style={{ backgroundColor: '#00004d', border: 'none' }}
                >
                  Complete Payment (PKR 5,000)
                </button>
              </div>
            </div>
          ) : (
            /* STATE B: NEW USER (No card at all -> Show Sales Page) */
            <>
              {/* --- YOUR ORIGINAL SALES CONTENT STARTS HERE --- */}
              <div className="text-center mb-5">
                <h1 className="fw-bold" style={{ color: '#1e3a8a' }}>The DDoctor Gold Standard</h1>
                <p className="lead text-muted mx-auto" style={{ maxWidth: '700px' }}>
                  More than just a discount card. It is your passport to affordable, premium healthcare
                  at Pakistan's top hospitals.
                </p>
              </div>

              <div className="row g-4 mb-5">
                {/* Feature 1 */}
                <div className="col-md-6">
                  <div className="p-4 h-100 border rounded-3 bg-light shadow-sm">
                    <h4 className="fw-bold text-primary mb-3">
                      <i className="bi bi-shield-check me-2"></i>
                      My DDoctor Membership
                    </h4>
                    <p className="text-secondary">
                      As an initiative to assist our valuable patients, DDoctor is delighted to announce
                      the <strong>Gold Membership</strong>. Card owners can now avail exclusive discounts
                      for themselves and their family members at 50+ partner hospitals.
                    </p>
                    <p className="text-secondary">
                      Moreover, your digital status is recognized instantly, meaning you don't have to wait
                      for the physical card to start saving.
                    </p>
                  </div>
                </div>

                {/* Feature 2 */}
                <div className="col-md-6">
                  <div className="p-4 h-100 border rounded-3 bg-white shadow-sm">
                    <h4 className="fw-bold text-primary mb-4">Why Upgrade to Gold?</h4>

                    <div className="d-flex align-items-start mb-3">
                      <div className="fs-3 text-warning me-3">🏷️</div>
                      <div>
                        <h6 className="fw-bold mb-1">Flat 10-50% Discounts</h6>
                        <small className="text-muted">Save significantly on OPD consultations, Lab Tests, and Surgeries.</small>
                      </div>
                    </div>

                    <div className="d-flex align-items-start mb-3">
                      <div className="fs-3 text-warning me-3">🚚</div>
                      <div>
                        <h6 className="fw-bold mb-1">Instant Activation</h6>
                        <small className="text-muted">No waiting for shipping. Your digital card is active immediately after payment.</small>
                      </div>
                    </div>

                    <div className="d-flex align-items-start">
                      <div className="fs-3 text-warning me-3">📅</div>
                      <div>
                        <h6 className="fw-bold mb-1">12-Month Validity</h6>
                        <small className="text-muted">Enjoy a full year of benefits with zero hidden renewal fees.</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Terms Alert */}
              <div className="alert alert-info d-flex align-items-center mb-5" role="alert">
                <i className="bi bi-info-circle-fill fs-4 me-3"></i>
                <div>
                  <strong>Note:</strong> This card is the property of DDoctor & is non-transferable.
                  Please present your Digital QR or Physical Card at the hospital reception to avail discounts.
                </div>
              </div>

              {/* BUY BUTTON SECTION */}
              <div className="text-center pb-5">
                <div className="card shadow border-0 mx-auto bg-white" style={{ maxWidth: '600px', borderRadius: '8px' }}>
                  <div className="card-body p-5">
                    <h3 className="fw-bold mb-3" style={{ color: '#0070E0' }}>
                      Unlock 50% Off at Hospitals
                    </h3>
                    <p className="fs-5 mb-4 text-dark">
                      Get the DDoctor Gold Card for just 5000 PKR/Year.
                    </p>
                    <div className="d-inline-block text-start mb-4">
                      <ul className="list-unstyled" style={{ fontSize: '1.1rem' }}>
                        <li className="mb-2">
                          <i className="bi bi-check-square-fill text-success me-2"></i>
                          Valid at 50+ Hospitals
                        </li>
                        <li className="mb-2">
                          <i className="bi bi-check-square-fill text-success me-2"></i>
                          Instant Digital Activation
                        </li>
                      </ul>
                    </div>

                    <button
                      className="btn btn-primary btn-lg px-5 py-3 fw-bold w-100"
                      style={{ borderRadius: '6px', background: '#00004d', borderColor: '#00004d' }}
                      onClick={() => setShowModal(true)}
                    >
                      Buy Now (5000 PKR)
                    </button>
                  </div>
                </div>
              </div>
              {/* --- END OF SALES CONTENT --- */}
            </>
          )}
        </>
      )}

      {/* =========================================================
          SECTION 2: ACTIVE GOLD MEMBER (Show The Blue Card)
         ========================================================= */}
      {isGoldMember && (
        <div className="pb-5">
          <h2 className="text-center mb-4 fw-bold text-success">Your Membership is Active!</h2>

          <div className="alert alert-info d-flex align-items-center mb-4 mx-auto" style={{ maxWidth: '600px' }} role="alert">
            <i className="bi bi-info-circle-fill fs-4 me-3"></i>
            <div>
              <strong>Note:</strong> Please present this Digital QR at the hospital reception to avail discounts.
            </div>
          </div>

          <div className="d-flex justify-content-center">
            <div className="gold-card-container">
              <div className="gold-card-bg">
                <div className="d-flex justify-content-between align-items-center w-100">
                  <span className="brand-logo">D.Doctor</span>
                  <span className="badge bg-warning text-dark">Gold Member</span>
                </div>

                <div className="text-center w-100">
                  <h2 className="card-number mb-0">{cardData?.cardNumber}</h2>
                  <span className="d-block text-white-50 lh-1">Virtual Health Card</span>
                  <div className="bg-white p-1 mt-2 d-inline-block rounded">
                    {cardData && (
                      <QRCode
                        value={JSON.stringify({ id: cardData.cardNumber, type: 'gold' })}
                        size={60}
                        viewBox={`0 0 60 60`}
                      />
                    )}
                  </div>
                </div>

                <div className="d-flex justify-content-between align-items-end w-100">
                  <div>
                    <small className="d-block text-white-50" style={{ fontSize: '0.6rem' }}>VALID THRU</small>
                    <strong>{cardData ? new Date(cardData.expiryDate).toLocaleDateString() : "..."}</strong>
                  </div>
                  <div className="text-end">
                    <small className="d-block text-white-50" style={{ fontSize: '0.6rem' }}>DISCOUNT</small>
                    <strong className="fs-5">10% OFF</strong>
                  </div>
                </div>

                <div className="live-indicator">
                  <span className="pulse-dot"></span> Active
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL */}
      <PurchaseCardModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onSuccess={() => { setShowModal(false); }}
      />
    </div>
  );

  // return (
  //   <div className="container mt-5">

  //     {/* =========================================================
  //            STATE A: NOT A MEMBER (Show Your Sales Content & Buy Button)
  //            ========================================================= */}
  //     {!isGoldMember && (
  //       <>
  //         {/* --- YOUR CUSTOM SALES CONTENT STARTS HERE --- */}
  //         <div className="text-center mb-5">
  //           <h1 className="fw-bold" style={{ color: '#1e3a8a' }}>The DDoctor Gold Standard</h1>
  //           <p className="lead text-muted mx-auto" style={{ maxWidth: '700px' }}>
  //             More than just a discount card. It is your passport to affordable, premium healthcare
  //             at Pakistan's top hospitals.
  //           </p>
  //         </div>

  //         <div className="row g-4 mb-5">
  //           {/* Feature 1: The Core Value */}
  //           <div className="col-md-6">
  //             <div className="p-4 h-100 border rounded-3 bg-light shadow-sm">
  //               <h4 className="fw-bold text-primary mb-3">
  //                 <i className="bi bi-shield-check me-2"></i>
  //                 My DDoctor Membership
  //               </h4>
  //               <p className="text-secondary">
  //                 As an initiative to assist our valuable patients, DDoctor is delighted to announce
  //                 the <strong>Gold Membership</strong>. Card owners can now avail exclusive discounts
  //                 for themselves and their family members at 50+ partner hospitals.
  //               </p>
  //               <p className="text-secondary">
  //                 Moreover, your digital status is recognized instantly, meaning you don't have to wait
  //                 for the physical card to start saving.
  //               </p>
  //             </div>
  //           </div>

  //           {/* Feature 2: Key Details */}
  //           <div className="col-md-6">
  //             <div className="p-4 h-100 border rounded-3 bg-white shadow-sm">
  //               <h4 className="fw-bold text-primary mb-4">Why Upgrade to Gold?</h4>

  //               <div className="d-flex align-items-start mb-3">
  //                 <div className="fs-3 text-warning me-3">🏷️</div>
  //                 <div>
  //                   <h6 className="fw-bold mb-1">Flat 10-50% Discounts</h6>
  //                   <small className="text-muted">Save significantly on OPD consultations, Lab Tests, and Surgeries.</small>
  //                 </div>
  //               </div>

  //               <div className="d-flex align-items-start mb-3">
  //                 <div className="fs-3 text-warning me-3">🚚</div>
  //                 <div>
  //                   <h6 className="fw-bold mb-1">Instant Activation</h6>
  //                   <small className="text-muted">No waiting for shipping. Your digital card is active immediately after payment.</small>
  //                 </div>
  //               </div>

  //               <div className="d-flex align-items-start">
  //                 <div className="fs-3 text-warning me-3">📅</div>
  //                 <div>
  //                   <h6 className="fw-bold mb-1">12-Month Validity</h6>
  //                   <small className="text-muted">Enjoy a full year of benefits with zero hidden renewal fees.</small>
  //                 </div>
  //               </div>
  //             </div>
  //           </div>
  //         </div>

  //         {/* Terms & Conditions (Small Print) */}
  //         <div className="alert alert-info d-flex align-items-center mb-5" role="alert">
  //           <i className="bi bi-info-circle-fill fs-4 me-3"></i>
  //           <div>
  //             <strong>Note:</strong> This card is the property of DDoctor & is non-transferable.
  //             Please present your Digital QR or Physical Card at the hospital reception to avail discounts.
  //           </div>
  //         </div>
  //         {/* --- YOUR CUSTOM SALES CONTENT ENDS HERE --- */}


  //         {/* BUY NOW BUTTON */}
  //         <div className="text-center pb-5">
  //           <div className="card shadow border-0 mx-auto bg-white" style={{ maxWidth: '600px', borderRadius: '8px' }}>
  //             <div className="card-body p-5">

  //               {/* Title */}
  //               <h3 className="fw-bold mb-3" style={{ color: '#0070E0' }}>
  //                 Unlock 50% Off at Hospitals
  //               </h3>

  //               {/* Subtitle */}
  //               <p className="fs-5 mb-4 text-dark">
  //                 Get the DDoctor Gold Card for just 5000 PKR/Year.
  //               </p>

  //               {/* Feature List with Checkmarks */}
  //               <div className="d-inline-block text-start mb-4">
  //                 <ul className="list-unstyled" style={{ fontSize: '1.1rem' }}>
  //                   <li className="mb-2">
  //                     <i className="bi bi-check-square-fill text-success me-2"></i>
  //                     Valid at 50+ Hospitals
  //                   </li>
  //                   <li className="mb-2">
  //                     <i className="bi bi-check-square-fill text-success me-2"></i>
  //                     Instant Digital Activation
  //                   </li>

  //                 </ul>
  //               </div>

  //               {/* Button */}
  //               <button
  //                 className="btn btn-primary btn-lg px-5 py-3 fw-bold w-100"
  //                 style={{ borderRadius: '6px', background: '#00004d', borderColor: '#00004d' }}
  //                 onClick={() => setShowModal(true)}
  //               >
  //                 Buy Now (5000 PKR)
  //               </button>
  //             </div>
  //           </div>
  //         </div>
  //       </>
  //     )}

  //     {/* =========================================================
  //            STATE B: ACTIVE MEMBER (Show The Blue Card)
  //            ========================================================= */}
  //     {isGoldMember && (
        
  //       <div className="pb-5">
  //         <h2 className="text-center mb-4 fw-bold text-success">Your Membership is Active!</h2>

  //         {/* Terms Alert */}
  //         <div className="alert alert-info d-flex align-items-center mb-4 mx-auto" style={{ maxWidth: '600px' }} role="alert">
  //           <i className="bi bi-info-circle-fill fs-4 me-3"></i>
  //           <div>
  //             <strong>Note:</strong> Please present this Digital QR at the hospital reception to avail discounts.
  //           </div>
  //         </div>

  //         <div className="d-flex justify-content-center">
  //           <div className="gold-card-container">
  //             <div className="gold-card-bg">
  //               {/* Header */}
  //               <div className="d-flex justify-content-between align-items-center w-100">
  //                 <span className="brand-logo">D.Doctor</span>
  //                 <span className="badge bg-warning text-dark">Gold Member</span>
  //               </div>

  //               {/* Number & QR */}
  //               <div className="text-center w-100">
  //                 <h2 className="card-number mb-0">{cardData?.cardNumber || "Generating..."}</h2>
  //                 <span className="d-block text-white-50 lh-1">Virtual Health Card</span>
  //                 <div className="bg-white p-1 mt-2 d-inline-block rounded">
  //                   {/* Safe check for QR Data */}
  //                   {cardData && (
  //                     <QRCode
  //                       value={JSON.stringify({ id: cardData.cardNumber, type: 'gold' })}
  //                       size={60}
  //                       viewBox={`0 0 60 60`}
  //                     />
  //                   )}
  //                 </div>
  //               </div>

  //               {/* Footer */}
  //               <div className="d-flex justify-content-between align-items-end w-100">
  //                 <div>
  //                   <small className="d-block text-white-50" style={{ fontSize: '0.6rem' }}>VALID THRU</small>
  //                   <strong>{cardData ? new Date(cardData.expiryDate).toLocaleDateString() : "..."}</strong>
  //                 </div>
  //                 <div className="text-end">
  //                   <small className="d-block text-white-50" style={{ fontSize: '0.6rem' }}>DISCOUNT</small>
  //                   <strong className="fs-5">10% OFF</strong>
  //                 </div>
  //               </div>

  //               {/* Status Dot */}
  //               <div className="live-indicator">
  //                 <span className="pulse-dot"></span> Active
  //               </div>
  //             </div>


  //           </div>
  //         </div>
  //       </div>
  //     )}

  //     {/* MODAL */}
  //     <PurchaseCardModal
  //       show={showModal}
  //       onClose={() => setShowModal(false)}
  //       onSuccess={() => { setShowModal(false); }}
  //     />
  //   </div>
  // );
};

export default DiscountCard;